/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2020 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import { DisplayKey } from '../../Game/DisplayKey';
import GameSetup from '../../Game/GameSetup';
import { CultureID } from '../../Localization/CultureID';
import StateComponent from '../../StateManager/StateComponent';
import InjectableComponent from '../Component/ComponentReference/InjectableComponent';
import OptionalDefinition from './OptionalDefinition';
import PrefabDefinition from './PrefabDefinition';
import PrefabLoaderBase from './PrefabLoaderBase';


/** @hidden */
const property: typeof cc._decorator.property = cc._decorator.property;

@cc._decorator.ccclass
export default class PrefabLoader extends PrefabLoaderBase
{
    private _gameSetupRef: InjectableComponent<GameSetup> = new InjectableComponent();

    protected setContexts(): void
    {
        super.setContexts();
        this._gameSetupRef.SetContext(GameSetup);
    }

    protected configureOptional(): void
    {
        const definitions: PrefabDefinition[] = this.prefabDefinitions.slice();
        this.prefabDefinitions = [];

        definitions.forEach(def =>
            {
                // No optional settings, re-add to defs to be instantiated on load.
                if (def.OptionalDefinitions.length < 1)
                {
                    this.prefabDefinitions.push(def);
                    return;
                }

                this.addOptionalDefinition(def);
            });
    }

    protected loadOptional(event: StateComponent): void
    {
        if (this.optionalMap.size < 1)
        {
            this.cleanup(event);
            return;
        }

        this.optionalMap.forEach((prefabDefinitions: PrefabDefinition[], displayKey: DisplayKey) =>
        {
            let shouldDisplay: boolean = false;

            const cultureID: CultureID =  this._gameSetupRef.WithValue(gameSetup =>
                {
                    shouldDisplay = gameSetup.ShouldDisplay(displayKey);
                    return gameSetup.Locale;
                });

            if (!shouldDisplay) { return; }

            const validLanguagePrefabs: PrefabDefinition[] = [];
            let validPrefabs: PrefabDefinition[] = [];

            // Build language and non-language arrays.
            prefabDefinitions.forEach(prefabDef =>
                {
                    const optionalDef: OptionalDefinition = prefabDef.GetOptional(displayKey);
                    if (!optionalDef) { return; }
                    if (!optionalDef.LanguageSpecific)
                    {
                        validPrefabs.push(prefabDef);
                        return;
                    }
                    if (optionalDef.Language !== cultureID) { return; }
                    validLanguagePrefabs.push(prefabDef);
                });

            // Removed instances where a language exists.
            validPrefabs = validPrefabs
                .filter(def => !validLanguagePrefabs.some(validDef => def.InstanceID === validDef.InstanceID));
            validPrefabs.push(...validLanguagePrefabs);

            this.prefabDefinitions = validPrefabs;
            this.loadSerial();
        });
    }

    /** Add to optional map and load at loading state. */
    private addOptionalDefinition(prefabDef: PrefabDefinition): void
    {
        prefabDef.OptionalDefinitions.forEach(optDef =>
            {
                const defs: PrefabDefinition[] = this.optionalMap.get(optDef.DisplayKey);
                if (!defs)
                {
                    this.optionalMap.set(optDef.DisplayKey, [prefabDef]);
                    return;
                }
                defs.push(prefabDef);
            });
    }
}
